import {
	useLoaderData,
	useLocation,
	useNavigate,
	useNavigation,
} from '@remix-run/react'
import { motion } from 'framer-motion'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import PlentyBody from '#app/components/typography/plenty-body'
import { Checkbox } from '#app/components/ui/checkbox'
import {
	DropdownMenu,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu'
import { Label } from '#app/components/ui/label'
import { type ClerkFacet } from '#app/constants/clerk-api'
import { FilterButton } from '#app/features/plp-clerk/clerk-components/filters/filter-button'
import { type loader } from '#app/routes/_public+/products+/$slug'
import FilterDropdownContent from './filter-dropdown-content'

export function GenderDropdown() {
	const { t } = useTranslation('product_list_page')

	const [isOpen, setisOpen] = useState(false)
	const data = useLoaderData<typeof loader>()
	const location = useLocation()
	const navigate = useNavigate()
	const navigation = useNavigation()

	const toggleDropdown = () => {
		setisOpen(!isOpen)
	}

	const genderFacets = (data.facetsData?.facets?.gender as ClerkFacet[]).filter(
		facet => facet.value !== 'Unisex',
	)

	const unisexFacet = data.facetsData?.facets?.gender.find(
		(facet: ClerkFacet) => facet.value === 'Unisex',
	)

	const getTotalCount = (facet: ClerkFacet) => {
		return facet.count + (unisexFacet ? unisexFacet.count : 0)
	}

	const getSelectedCount = () => {
		return (
			new URLSearchParams(
				navigation.state === 'idle'
					? location.search
					: navigation.location.search,
			).getAll('gender').length - 1
		)
	}

	const handleCheckboxChange = (
		checked: boolean,
		term: string[],
		key: string,
	) => {
		const params = new URLSearchParams(
			navigation.state === 'idle'
				? location.search
				: navigation.location.search,
		)
		if (checked) {
			params.append(key, term[0])
			if (!params.getAll(key).includes('Unisex')) params.append(key, 'Unisex')
		} else {
			const values = params.getAll(key)
			const newValues = values.filter(value => value !== term[0])

			params.delete(key)
			newValues.forEach(value => {
				params.append(key, value)
			})
			if (params.getAll(key).length === 1) params.delete(key)
		}
		params.delete('p')

		navigate({
			pathname: '.',
			search: params.toString(),
		})
	}

	return (
		<DropdownMenu onOpenChange={toggleDropdown}>
			<DropdownMenuTrigger asChild>
				<FilterButton
					selectedCount={getSelectedCount()}
					isSelectorOpen={isOpen}
					text={t('gender')}
					className="text-black-90"
				/>
			</DropdownMenuTrigger>

			<FilterDropdownContent>
				<motion.div
					initial={{ opacity: 0, maxHeight: 0 }}
					animate={{ opacity: 1, maxHeight: '240px' }}
					exit={{ opacity: 0, maxHeight: 0 }}
					transition={{ duration: 0.3, ease: 'linear' }}
				>
					<div className="flex flex-col">
						<div className="gap-4">
							{genderFacets.map((facet: ClerkFacet) => (
								<Label
									className="flex items-center px-4 py-2 hover:bg-beige-70"
									key={facet.value}
									htmlFor={facet.value}
								>
									<Checkbox
										className="mr-2 h-6 w-6"
										id={facet.value}
										value={facet.value}
										name="gender"
										defaultChecked={new URLSearchParams(
											navigation.state === 'idle'
												? location.search
												: navigation.location.search,
										)
											.getAll('gender')
											.includes(facet.value)}
										onCheckedChange={checked =>
											handleCheckboxChange(checked, [facet.value], 'gender')
										}
									/>

									<div className="flex w-full justify-between gap-6">
										<PlentyBody size="sm">
											{facet.value === 'Herre' || facet.value === 'Male'
												? t('gender_boy')
												: t('gender_girl')}
										</PlentyBody>
										<PlentyBody size="sm" className="text-green-40">
											{getTotalCount(facet)}
										</PlentyBody>
									</div>
								</Label>
							))}
						</div>
					</div>
				</motion.div>
			</FilterDropdownContent>
		</DropdownMenu>
	)
}
